import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAdminSubPlans, createAdminSubPlan,
  updateAdminSubPlan, toggleAdminSubPlan
} from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";
const COLORS = ["#a7cc48", "#4fb0c6", "#f4a261", "#9b5de5", "#e63946", "#2ec4b6"];

const INTERVAL_OPTIONS = [
  { value: "monthly",    label: "Monthly",          hint: "Billed every month" },
  { value: "quarterly",  label: "Quarterly",         hint: "Billed every 3 months" },
  { value: "biannually", label: "Bi-annually",       hint: "Billed every 6 months" },
  { value: "annually",   label: "Annually",          hint: "Billed every 12 months" },
];

const emptyInterval = { interval: "monthly", price: "" };
const emptyForm = {
  name: "", tagline: "", color: PRIMARY, badge: false, active: true,
  offerings: [], intervals: [{ ...emptyInterval }]
};

// Reads intervals from a plan doc — handles both old (price+interval) and new (intervals[]) shapes
const intervalsFromPlan = (plan) => {
  if (plan.intervals && plan.intervals.length > 0) {
    return plan.intervals.map((i) => ({
      interval: i.interval,
      price: String(i.price),
      paystackPlanCode: i.paystackPlanCode || "",
      active: i.active !== false
    }));
  }
  // Legacy single-interval plan
  return [{ interval: plan.interval || "monthly", price: plan.price || "", paystackPlanCode: plan.paystackPlanCode || "", active: true }];
};

export default function SubscriptionPlans() {
  const dispatch = useDispatch();
  const plans = useSelector((s) => s.profile.adminSubPlans);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [offerInput, setOfferInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => { dispatch(getAdminSubPlans()); }, [dispatch]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setMsg(null); setShowModal(true); };
  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      name: plan.name || "", tagline: plan.tagline || "",
      color: plan.color || PRIMARY, badge: !!plan.badge, active: plan.active !== false,
      offerings: (plan.offerings || []).map((o) => (typeof o === "string" ? o : o.offer || "")),
      intervals: intervalsFromPlan(plan)
    });
    setMsg(null);
    setShowModal(true);
  };

  // ── Interval row helpers ──────────────────────────────────────────────────
  const addInterval = () => {
    const used = form.intervals.map((i) => i.interval);
    const next = INTERVAL_OPTIONS.find((o) => !used.includes(o.value));
    if (!next) return; // all 4 already added
    setForm((f) => ({ ...f, intervals: [...f.intervals, { interval: next.value, price: "", active: true }] }));
  };
  const removeInterval = (idx) => {
    if (form.intervals.length <= 1) return;
    setForm((f) => ({ ...f, intervals: f.intervals.filter((_, i) => i !== idx) }));
  };
  const updateInterval = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      intervals: f.intervals.map((item, i) => i === idx ? { ...item, [field]: value } : item)
    }));
  };

  // Prevent selecting the same interval type twice
  const availableForRow = (currentValue) => {
    const used = form.intervals.map((i) => i.interval).filter((v) => v !== currentValue);
    return INTERVAL_OPTIONS.filter((o) => !used.includes(o.value));
  };

  // ── Offering helpers ──────────────────────────────────────────────────────
  const addOffer = () => {
    if (!offerInput.trim()) return;
    setForm((f) => ({ ...f, offerings: [...f.offerings, offerInput.trim()] }));
    setOfferInput("");
  };
  const removeOffer = (i) => setForm((f) => ({ ...f, offerings: f.offerings.filter((_, idx) => idx !== i) }));

  // ── Submit ────────────────────────────────────────────────────────────────
  const submit = async () => {
    if (!form.name.trim()) return setMsg({ type: "error", text: "Plan name is required" });
    const validIntervals = form.intervals.filter((i) => i.interval && i.price && Number(i.price) > 0);
    if (validIntervals.length === 0) return setMsg({ type: "error", text: "At least one billing interval with a price is required" });

    setSubmitting(true); setMsg(null);
    const body = {
      name: form.name,
      tagline: form.tagline,
      color: form.color,
      badge: form.badge,
      active: form.active,
      offerings: form.offerings.map((o, i) => ({ id: i + 1, offer: o, status: true })),
      intervals: validIntervals.map((i) => ({ interval: i.interval, price: Number(i.price), active: i.active !== false }))
    };
    const res = editing
      ? await dispatch(updateAdminSubPlan(editing._id, body))
      : await dispatch(createAdminSubPlan(body));
    setSubmitting(false);
    if (res?.success) {
      setMsg({ type: "success", text: editing ? "Plan updated" : "Plan created and synced to payment processor" });
      dispatch(getAdminSubPlans());
      setTimeout(() => setShowModal(false), 900);
    } else {
      setMsg({ type: "error", text: res?.message || "Something went wrong" });
    }
  };

  const toggle = async (plan) => {
    setTogglingId(plan._id);
    await dispatch(toggleAdminSubPlan(plan._id));
    dispatch(getAdminSubPlans());
    setTogglingId(null);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getIntervals = (plan) =>
    plan.intervals?.length > 0 ? plan.intervals : [{ interval: plan.interval || "monthly", price: plan.price, paystackPlanCode: plan.paystackPlanCode }];

  const lowestPrice = (plan) => {
    const intervals = getIntervals(plan);
    return Math.min(...intervals.map((i) => Number(i.price) || 0));
  };

  const intervalLabel = (v) => INTERVAL_OPTIONS.find((o) => o.value === v)?.label || v;

  return (
    <div style={S.root}>
      <div style={S.pageHeader}>
        <div>
          <h2 style={S.heading}>Subscription Plans</h2>
          <p style={S.headingSub}>Each plan can have multiple billing intervals — each gets its own Paystack plan code</p>
        </div>
        <button style={S.btn} onClick={openCreate}>
          <i className="bi bi-plus-lg" style={{ marginRight: 6 }} />New Plan
        </button>
      </div>

      {plans.length === 0 && (
        <div style={S.emptyState}>
          <i className="bi bi-card-list" style={{ fontSize: 48, color: "#ddd" }} />
          <p style={{ color: "#aaa", marginTop: 14 }}>No plans yet. Create the first one.</p>
          <button style={S.btn} onClick={openCreate}>Create Plan</button>
        </div>
      )}

      <div style={S.grid}>
        {plans.map((plan) => {
          const intervals = getIntervals(plan);
          return (
            <div key={plan._id} style={{ ...S.planCard, borderTop: `3px solid ${plan.color || PRIMARY}`, opacity: plan.active ? 1 : 0.55 }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: BG }}>{plan.name}</div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{plan.tagline}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                  {plan.badge && <span style={S.popularTag}>Popular</span>}
                  <span style={{ ...S.statusPill, background: plan.active ? "rgba(46,196,182,0.12)" : "#f5f5f5", color: plan.active ? "#2ec4b6" : "#aaa" }}>
                    {plan.active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              <div style={{ fontSize: 13, color: "#888", marginBottom: 10 }}>
                from <span style={{ fontWeight: 800, fontSize: 20, color: plan.color || PRIMARY }}>₦{Number(lowestPrice(plan)).toLocaleString("en-NG")}</span>
              </div>

              {/* Interval chips */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                {intervals.map((iv, idx) => (
                  <div key={idx} style={S.intervalChip}>
                    <span style={{ fontWeight: 600, color: BG }}>₦{Number(iv.price).toLocaleString("en-NG")}</span>
                    <span style={{ fontSize: 11, color: "#888", marginLeft: 4 }}>/ {intervalLabel(iv.interval)}</span>
                    {iv.paystackPlanCode
                      ? <span style={S.syncedDot} title={iv.paystackPlanCode}>✓</span>
                      : <span style={{ ...S.syncedDot, background: "#f0f0f0", color: "#bbb" }} title="Not yet synced">!</span>
                    }
                  </div>
                ))}
              </div>

              <ul style={S.offerList}>
                {(plan.offerings || []).slice(0, 4).map((o, i) => (
                  <li key={i} style={S.offerItem}>
                    <i className="bi bi-check-circle-fill" style={{ color: plan.color || PRIMARY, marginRight: 6, fontSize: 11, flexShrink: 0 }} />
                    {typeof o === "string" ? o : o.offer}
                  </li>
                ))}
                {(plan.offerings || []).length > 4 && (
                  <li style={{ ...S.offerItem, color: "#aaa" }}>+{plan.offerings.length - 4} more</li>
                )}
              </ul>

              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button style={S.editBtn} onClick={() => openEdit(plan)}>
                  <i className="bi bi-pencil" style={{ marginRight: 4 }} />Edit
                </button>
                <button
                  style={{ ...S.editBtn, color: plan.active ? "#e63946" : "#2ec4b6", borderColor: plan.active ? "#e63946" : "#2ec4b6" }}
                  onClick={() => toggle(plan)} disabled={togglingId === plan._id}
                >
                  {togglingId === plan._id ? "…" : plan.active ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Create / Edit modal ─────────────────────────────────────────────── */}
      {showModal && (
        <div style={S.overlay} onClick={() => setShowModal(false)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={S.modalTitle}>{editing ? "Edit Plan" : "Create New Plan"}</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                  A separate Paystack plan is created for each billing interval
                </div>
              </div>
              <button style={S.closeBtn} onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div style={S.modalBody}>
              {/* Name + tagline */}
              <div style={S.fieldRow}>
                <div style={S.field}>
                  <label style={S.label}>Plan Name *</label>
                  <input style={S.input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Business" />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Tagline</label>
                  <input style={S.input} value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Short description" />
                </div>
              </div>

              {/* Color + flags */}
              <div style={S.fieldRow}>
                <div style={S.field}>
                  <label style={S.label}>Accent Color</label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                    {COLORS.map((c) => (
                      <div key={c} onClick={() => setForm({ ...form, color: c })} style={{ width: 26, height: 26, borderRadius: "50%", background: c, cursor: "pointer", border: form.color === c ? "3px solid #333" : "3px solid transparent", boxSizing: "border-box" }} />
                    ))}
                  </div>
                </div>
                <div style={{ ...S.field, display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 10, paddingBottom: 4 }}>
                  <label style={{ ...S.checkLabel, marginTop: 0 }}>
                    <input type="checkbox" checked={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.checked })} style={{ marginRight: 6 }} />
                    Show "Popular" badge
                  </label>
                  <label style={S.checkLabel}>
                    <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} style={{ marginRight: 6 }} />
                    Active (visible to merchants)
                  </label>
                </div>
              </div>

              {/* ── Billing intervals ──────────────────────────────────────── */}
              <div style={S.sectionDivider}>
                <span style={S.sectionDividerLabel}>Billing Intervals</span>
              </div>
              <p style={{ fontSize: 12, color: "#888", marginBottom: 12, marginTop: -4 }}>
                Each interval creates its own plan on Paystack. Merchants choose an interval at checkout.
              </p>

              {form.intervals.map((iv, idx) => (
                <div key={idx} style={S.intervalRow}>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <label style={S.label}>Interval</label>
                    <select
                      style={S.input}
                      value={iv.interval}
                      onChange={(e) => updateInterval(idx, "interval", e.target.value)}
                    >
                      {availableForRow(iv.interval).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>
                      {INTERVAL_OPTIONS.find((o) => o.value === iv.interval)?.hint}
                    </div>
                  </div>

                  <div style={{ flex: 1, minWidth: 130 }}>
                    <label style={S.label}>Price (₦) *</label>
                    <input
                      style={S.input}
                      type="number"
                      min="0"
                      placeholder="e.g. 3000"
                      value={iv.price}
                      onChange={(e) => updateInterval(idx, "price", e.target.value)}
                    />
                  </div>

                  {/* Paystack plan code badge (read-only, set by backend) */}
                  <div style={{ flex: 1.2, minWidth: 140 }}>
                    <label style={S.label}>Paystack Plan Code</label>
                    <div style={S.codeBox}>
                      {iv.paystackPlanCode
                        ? <><i className="bi bi-check-circle-fill" style={{ color: "#2ec4b6", marginRight: 6, fontSize: 12 }} />{iv.paystackPlanCode}</>
                        : <span style={{ color: "#bbb" }}>Auto-assigned on save</span>
                      }
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", paddingBottom: 2, gap: 4 }}>
                    <label style={{ ...S.checkLabel, fontSize: 11 }}>
                      <input type="checkbox" checked={iv.active !== false} onChange={(e) => updateInterval(idx, "active", e.target.checked)} style={{ marginRight: 4 }} />
                      Active
                    </label>
                    {form.intervals.length > 1 && (
                      <button style={S.removeIntervalBtn} onClick={() => removeInterval(idx)} title="Remove interval">
                        <i className="bi bi-trash3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {form.intervals.length < 4 && (
                <button style={S.addIntervalBtn} onClick={addInterval}>
                  <i className="bi bi-plus-lg" style={{ marginRight: 6 }} />Add Another Interval
                </button>
              )}

              {/* ── Offerings ──────────────────────────────────────────────── */}
              <div style={S.sectionDivider}>
                <span style={S.sectionDividerLabel}>Features / Offerings</span>
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <input
                  style={{ ...S.input, flex: 1 }}
                  placeholder="Type a feature and press Add…"
                  value={offerInput}
                  onChange={(e) => setOfferInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addOffer()}
                />
                <button style={S.btn} onClick={addOffer}>Add</button>
              </div>
              <div style={S.offerTags}>
                {form.offerings.map((o, i) => (
                  <span key={i} style={S.offerTag}>
                    {o}
                    <button style={S.removeTag} onClick={() => removeOffer(i)}>✕</button>
                  </span>
                ))}
              </div>

              {msg && (
                <div style={{ borderRadius: 8, padding: "10px 14px", fontSize: 13, marginTop: 14, background: msg.type === "success" ? "rgba(46,196,182,0.1)" : "rgba(230,57,70,0.08)", color: msg.type === "success" ? "#2ec4b6" : "#e63946", border: `1px solid ${msg.type === "success" ? "#2ec4b6" : "#e63946"}` }}>
                  {msg.text}
                </div>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
                <button style={S.btn} onClick={submit} disabled={submitting}>
                  {submitting ? "Saving & syncing to Paystack…" : editing ? "Update Plan" : "Create Plan"}
                </button>
                <button style={S.btnSecondary} onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 28 },
  heading: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  headingSub: { fontSize: 13, color: "#888", marginTop: 4 },
  btn: { background: BG, border: "none", borderRadius: 8, color: "#fff", fontWeight: 600, padding: "8px 20px", cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center" },
  btnSecondary: { background: "#f5f5f5", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#666", fontWeight: 500, padding: "8px 16px", cursor: "pointer", fontSize: 13 },
  emptyState: { textAlign: "center", padding: "60px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 20 },
  planCard: { background: "#fff", borderRadius: 12, padding: 22, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", position: "relative" },
  popularTag: { background: "rgba(167,204,72,0.15)", color: "#5a8a00", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, border: "1px solid " + PRIMARY },
  statusPill: { borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 },
  intervalChip: { display: "flex", alignItems: "center", background: "#f5f5f5", borderRadius: 20, padding: "4px 10px", fontSize: 12 },
  syncedDot: { marginLeft: 6, background: "rgba(46,196,182,0.15)", color: "#2ec4b6", borderRadius: "50%", width: 16, height: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 },
  offerList: { listStyle: "none", padding: 0, margin: "10px 0 0", display: "flex", flexDirection: "column", gap: 5 },
  offerItem: { fontSize: 12, color: "#555", display: "flex", alignItems: "flex-start" },
  editBtn: { flex: 1, background: "#f9f9f9", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#555", padding: "7px 0", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 500 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", overflowY: "auto", padding: "20px 0" },
  modal: { background: "#fff", borderRadius: 12, width: 680, maxWidth: "95vw", maxHeight: "92vh", overflow: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.15)", margin: "auto" },
  modalHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 24px", borderBottom: "1px solid #f0f0f0", position: "sticky", top: 0, background: "#fff", zIndex: 1 },
  modalTitle: { fontSize: 16, fontWeight: 700, color: BG },
  closeBtn: { background: "none", border: "none", color: "#aaa", fontSize: 18, cursor: "pointer", lineHeight: 1 },
  modalBody: { padding: "20px 24px 32px" },
  fieldRow: { display: "flex", gap: 14, flexWrap: "wrap" },
  field: { flex: 1, minWidth: 200 },
  label: { display: "block", color: "#555", fontSize: 13, fontWeight: 600, marginBottom: 6, marginTop: 12 },
  input: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "9px 12px", fontSize: 13, color: "#333", background: "#fafafa", width: "100%", boxSizing: "border-box", outline: "none" },
  checkLabel: { fontSize: 13, color: "#555", cursor: "pointer", display: "flex", alignItems: "center", marginTop: 12 },
  sectionDivider: { borderTop: "1px solid #f0f0f0", margin: "20px 0 14px", position: "relative", display: "flex", alignItems: "center" },
  sectionDividerLabel: { background: "#fff", paddingRight: 10, fontSize: 12, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.8, position: "absolute", top: -9, left: 0 },
  intervalRow: { display: "flex", gap: 12, alignItems: "flex-start", background: "#fafafa", border: "1.5px solid #f0f0f0", borderRadius: 10, padding: "14px 16px", marginBottom: 10, flexWrap: "wrap" },
  codeBox: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "9px 12px", fontSize: 12, color: "#555", background: "#f9f9f9", fontFamily: "monospace", display: "flex", alignItems: "center", minHeight: 38, boxSizing: "border-box" },
  removeIntervalBtn: { background: "none", border: "1px solid #e0e0e0", borderRadius: 6, color: "#e63946", cursor: "pointer", padding: "4px 8px", fontSize: 13 },
  addIntervalBtn: { background: "#fff", border: "1.5px dashed #d0d0d0", borderRadius: 8, color: "#888", cursor: "pointer", padding: "8px 16px", fontSize: 13, display: "inline-flex", alignItems: "center", marginBottom: 4 },
  offerTags: { display: "flex", flexWrap: "wrap", gap: 6, minHeight: 32 },
  offerTag: { background: "#f0f0f0", borderRadius: 20, padding: "4px 12px 4px 14px", fontSize: 12, color: "#555", display: "flex", alignItems: "center", gap: 6 },
  removeTag: { background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: 11, padding: 0, lineHeight: 1 },
};
